import { ApplicationRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  ActivatedRoute,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { ThemeService } from './services/theme.service';
import { GeolocationService } from './services/geolocation.service';
import { SessionStorageService } from './services/session-storage.service';
import { HttpClient } from '@angular/common/http';
import { MetadataService } from './services/metadata.service';
import { first } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import {
  forceApplyNewBuild,
  isOutsideFunnelRoute,
  onBuildUpdateDeferred,
  startVersionPolling,
} from './utils/app-version';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  isLoading = true;

  pendingGeolocationSubscription: Subscription = new Subscription();
  isPendingGeolocation: boolean = false;
  private stopVersionPolling: (() => void) | null = null;
  private stopDeferredListener: (() => void) | null = null;
  private updateSnackRef: MatSnackBarRef<TextOnlySnackBar> | null = null;

  constructor(
    private router: Router,
    private themeService: ThemeService,
    private geolocationService: GeolocationService,
    private metadataService: MetadataService,
    private localStorageService: SessionStorageService,
    private route: ActivatedRoute,
    private http: HttpClient,
    private appRef: ApplicationRef,
    private snackBar: MatSnackBar
  ) {
    this.router.events.subscribe((e: Event) => {
      this.navigationInterceptor(e);
    });
  }

  ngOnInit(): void {
    const envConfig = this.localStorageService.getEnvConfig();

    const pollIntervalMs =
      envConfig.versionPollIntervalMs ?? environment.versionPollIntervalMs;
    this.stopVersionPolling = startVersionPolling(pollIntervalMs, {
      isSafeRoute: () => isOutsideFunnelRoute(this.router.url),
    });

    this.stopDeferredListener = onBuildUpdateDeferred(() => {
      this.showUpdateAvailableSnack();
    });

    if (envConfig.obrigarGeolocalizacao) {
      this.geolocationService.getPosition();
    }

    this.metadataService.getDeviceInfo();

    this.route.queryParams.pipe(first()).subscribe(({ theme, productId }) => {
      const emissor = theme || this.themeService.getThemeFromUrl();
      if (emissor) {
        this.themeService.setTheme(emissor, envConfig.productId);
      }
    });

    if (envConfig.urlStart === true) {
      this.router.navigate(['startTrue']);
    }
  }

  ngOnDestroy(): void {
    if (this.stopVersionPolling) {
      this.stopVersionPolling();
      this.stopVersionPolling = null;
    }
    if (this.stopDeferredListener) {
      this.stopDeferredListener();
      this.stopDeferredListener = null;
    }
    this.updateSnackRef?.dismiss();
  }

  private showUpdateAvailableSnack(): void {
    if (this.updateSnackRef) {
      return;
    }

    this.updateSnackRef = this.snackBar.open(
      'Nova versão disponível. Finalize o cadastro ou atualize agora.',
      'Atualizar',
      {
        duration: 0,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      }
    );

    this.updateSnackRef.onAction().subscribe(() => {
      void forceApplyNewBuild();
    });

    this.updateSnackRef.afterDismissed().subscribe(() => {
      this.updateSnackRef = null;
    });
  }

  navigationInterceptor(event: Event): void {
    if (event instanceof NavigationStart) {
      this.isLoading = true;
    }
    if (event instanceof NavigationEnd) {
      this.isLoading = false;
    }
    if (event instanceof NavigationCancel) {
      this.isLoading = false;
    }
    if (event instanceof NavigationError) {
      this.isLoading = false;
    }
  }
}
